import React, { FunctionComponent } from 'react'
import { Avatar, Heading, Loader, Text } from '@afterpaytouch/core'
import { useConsumerName, useConsumerSelector } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { TrackOnMount } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import styles from './style.module.css'

/**
 * CASHDAP-3223: the "Welcome back" recognition transient (Variant A) shown on the LoggedIn
 * interstitial when a returning AP-cohort consumer is silently recognized via their Auto Remember Me
 * cookie. It's a brief branded screen — avatar, name, "Recognized on this device", spinner — that
 * displays while the existing silent-login/start-checkout flow completes and auto-advances to payment.
 * Gated by useShouldShowWelcomeBackRecognition (flag + recognized + AP-eligible).
 *
 * The "Not you?" escape hatch was removed from this transient (2026-08-05 product decision); a
 * mis-recognized consumer's escape is now the account modal's Log out (AccountModal, reachable from
 * the header profile chip on every post-recognition page), which ends the remember-me session. The
 * risk step-up (207 → verify) is handled upstream by persistentLoginOrchestrator, which routes an
 * `active && verify` resume to Route.VERIFY before this transient ever renders.
 */
export const WelcomeBackRecognition: FunctionComponent = () => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const { email, profile } = useConsumerSelector()
  // Prefer the consumer profile's dedicated givenNames field (accountLookup stores it under
  // state.profile); fall back to the first token of the full display name when it isn't populated.
  // accountLookup copies givenNames verbatim, so treat blank/whitespace-only (e.g. surname-only
  // profiles) as missing via the trimmed truthy check rather than ?? (which keeps an empty string).
  // The display name is itself getConsumerName(givenNames + surname), so trim/collapse it before
  // taking the first token — a whitespace-only givenNames would otherwise leave a leading-space name.
  const firstName = profile?.givenNames?.trim() || consumerName.trim().split(/\s+/)[0]
  const { textSize, headingSize, smallTextSize } = useCheckoutSizes()

  return (
    <div className={styles.container} data-testid='auto-remember-me-welcome-back'>
      <TrackOnMount eventName={TrackingEvent.VIEWED_AUTO_REMEMBER_ME_PAGE} />
      <MaskedElement actionName='Auto Remember Me Welcome Back'>
        {/* MaskedElement's div offers no styling hook, and the block-level Avatar left-aligns inside
            it (text-align only centers the inline text siblings) — center it explicitly. */}
        <div className={styles.avatar}>
          <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
        </div>
        <Heading size={headingSize}>{t('common:rememberMeWelcomeBack', { name: firstName })}</Heading>
        {/* Heading/Text have zero default margins, so the wrapper's margin is the exact 16px gap. */}
        <div className={styles.email}>
          <Text size={textSize}>{email}</Text>
        </div>
      </MaskedElement>
      <div className={styles.recognized}>
        <span className={styles.check} aria-hidden='true'>
          ✓
        </span>
        <Text size={smallTextSize}>{t('common:rememberMeRecognizedDevice')}</Text>
      </div>
      <Loader />
      <Text size={textSize}>{t('common:rememberMeLoggingInSecurely')}</Text>
    </div>
  )
}
